<template>
<aside class="representative-documents" :class="{ 'creation-step-right': rightDisabled }" :aria-disabled="rightDisabled">
        <InlineMessage v-if="rightDisabled" severity="warn" class="creation-step-notice" role="status">{{ creationStep ? 'Crea prima il nuovo rappresentante e il suo contatto.' : 'Cerca o crea prima il rappresentante.' }} Dopo la verifica potrai gestire i clienti e i contratti, compresi quelli già selezionati.</InlineMessage>
        <section class="client-selection" :inert="rightDisabled">
          <h5 class="section-heading">Ricerca clienti <small>({{ selectedClients.length }} selezionati)</small></h5>
          <InlineMessage severity="info" class="client-selection-message">Quando cerchi un cliente o lo importi da MindTab, vengono selezionati automaticamente solo i contratti attivi non già associati al contatto scelto. Puoi modificare la selezione.</InlineMessage>
          <label for="cambio-rapp-cliente">Aggiungi un cliente non domestico</label>
          <AutoComplete
            id="cambio-rapp-cliente"
            v-model="clientSearch"
            :suggestions="clientSuggestions"
            optionLabel="searchLabel"
            :minLength="2"
            :delay="300"
            :inputClass="'w-full'"
            class="w-full"
            panelClass="cambio-rappresentante-client-suggestions"
            placeholder="Cerca per nome, codice, CF o partita IVA"
            :disabled="rightDisabled || busy || clientAdding"
            @complete="searchClients"
            @item-select="addClient"
          >
            <template #option="{ option }">
              <div class="client-suggestion" :class="{ 'already-associated': clientHasRepresentative(option) }">
                <strong>{{ option.ragsoc || option.code || option.id }}</strong>
                <span>Codice: {{ option.code || '—' }}</span>
                <span>CF: {{ option.codfis || '—' }}</span>
                <span>P. IVA: {{ option.piva || '—' }}</span>
                <span class="client-suggestion-status" :class="{ 'already-associated': clientHasRepresentative(option) }">{{ representativeStatus(option) }}</span>
              </div>
            </template>
          </AutoComplete>
          <small v-if="clientSearchBusy || clientAdding" role="status">Ricerca o caricamento cliente in corso...</small>
          <small v-if="!selectedClients.length" class="p-error">Seleziona almeno un cliente.</small>
        </section>
        <section class="client-contract-selection" :inert="rightDisabled">
          <h5 class="section-heading">Contratti <small>({{ selectedContractIds.length }} selezionati)</small></h5>
          <div class="client-contract-cards">
            <div v-for="cliente in selectedClients" :key="cliente.id" class="client-contract-item">
              <button type="button" class="client-contract-remove" :aria-label="`Rimuovi ${cliente.nome} e i suoi contratti dalla selezione`" :disabled="rightDisabled || busy || clientAdding" v-tooltip="'Rimuovi cliente e contratti dalla selezione'" @click.stop="removeClient(cliente.id)"><i class="pi pi-times" aria-hidden="true"></i></button>
              <button type="button" class="client-contract-card" :disabled="rightDisabled || busy || clientAdding || !contractsOfClient(cliente.id).length" v-tooltip="contractsOfClient(cliente.id).length ? 'Visualizza e seleziona i contratti associati' : null" @click="contractDialogClientId = cliente.id">
                <strong>{{ cliente.nome }}</strong>
                <span>CF/P. IVA: {{ clientIdentifier(cliente) }}</span>
                <span class="client-representative-status" :class="{ unchanged: clientHasRepresentative(cliente) }">{{ representativeStatus(cliente) }}</span>
                <span v-if="contractsOfClient(cliente.id).length">{{ activeContractCount(cliente.id) }} contratti attivi su {{ contractsOfClient(cliente.id).length }} totali</span>
                <span v-if="selectedCount(cliente.id)" class="client-contract-selected">{{ selectedCount(cliente.id) }} contratti selezionati su {{ contractsOfClient(cliente.id).length }}</span>
                <span v-else-if="clientHasRepresentative(cliente)" class="client-contract-only-representative">Nessuna modifica a rappresentante e contratti</span>
                <span v-else-if="!contractsOfClient(cliente.id).length" class="client-contract-only-representative" v-tooltip="'Questo cliente subirà solo il cambio rappresentante perché non ha contratti.'">Solo cambio rappresentante: nessun contratto</span>
                <span v-else class="client-contract-only-representative" v-tooltip="'Questo cliente subirà solo il cambio rappresentante perché non hai selezionato contratti per il cambio contatto.'">Solo cambio rappresentante: nessun contratto selezionato</span>
                <i v-if="contractsOfClient(cliente.id).length" class="pi pi-chevron-right client-contract-open" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </section>
        <InlineMessage severity="info" class="save-description" :inert="rightDisabled">Il salvataggio aggiorna il rappresentante solo dei clienti che non lo hanno già e il contatto dei contratti selezionati.</InlineMessage>
      </aside>
</template>

<script>
import AutoComplete from 'primevue/autocomplete'
import InlineMessage from 'primevue/inlinemessage'

export default {
  components: { AutoComplete, InlineMessage },
  inject: ['representativeDialog'],
  computed: {
    rightDisabled() { return this.representativeDialog.rightDisabled },
    creationStep() { return this.representativeDialog.creationStep },
    selectedClients() { return this.representativeDialog.selectedClients },
    selectedContractIds() { return this.representativeDialog.selectedContractIds },
    clientSearch: { get() { return this.representativeDialog.clientSearch }, set(value) { this.representativeDialog.clientSearch = value } },
    clientSuggestions() { return this.representativeDialog.clientSuggestions },
    busy() { return this.representativeDialog.busy },
    clientAdding() { return this.representativeDialog.clientAdding },
    clientSearchBusy() { return this.representativeDialog.clientSearchBusy },
    contractDialogClientId: { get() { return this.representativeDialog.contractDialogClientId }, set(value) { this.representativeDialog.contractDialogClientId = value } },
  },
  methods: {
    clientHasRepresentative(...args) { return this.representativeDialog.clientHasRepresentative(...args) },
    representativeStatus(cliente) {
      if (this.clientHasRepresentative(cliente)) return 'Rappresentante già associato: non verrà modificato'
      const details = [
        cliente.rappresentante_attuale_nome,
        cliente.rappresentante_attuale_id != null && `ID: ${cliente.rappresentante_attuale_id}`,
        cliente.rappresentante_attuale_codfis && `CF: ${cliente.rappresentante_attuale_codfis}`
      ].filter(Boolean)
      if (details.length) return `Cambio rappresentante previsto (attuale: ${details.join(' · ')})`
      const currentId = Number(cliente.cod_rapp ?? cliente.info?.cod_rapp)
      return currentId > 0
        ? `Cambio rappresentante previsto (attuale: ID ${currentId}, dati del rappresentante non trovati)`
        : 'Cambio rappresentante previsto (nessun rappresentante attuale)'
    },
    searchClients(...args) { return this.representativeDialog.searchClients(...args) },
    addClient(...args) { return this.representativeDialog.addClient(...args) },
    removeClient(...args) { return this.representativeDialog.removeClient(...args) },
    contractsOfClient(...args) { return this.representativeDialog.contractsOfClient(...args) },
    clientIdentifier(...args) { return this.representativeDialog.clientIdentifier(...args) },
    activeContractCount(...args) { return this.representativeDialog.activeContractCount(...args) },
    selectedCount(...args) { return this.representativeDialog.selectedCount(...args) },
  }
}
</script>
